import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { delegationsAPI, tasksAPI, usersAPI, aiAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Users, 
  Sparkles, 
  ArrowRight,
  CheckCircle2,
  Clock,
  RefreshCw,
  UserPlus,
  Loader2
} from 'lucide-react';

const DelegationPage = () => {
  const { isOwner } = useAuth();
  const [delegations, setDelegations] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [delegationsRes, tasksRes, usersRes] = await Promise.all([
        delegationsAPI.getAll(),
        tasksAPI.getAll({ status: 'pending' }),
        usersAPI.getAll().catch(() => ({ data: [] }))
      ]);
      
      setDelegations(delegationsRes.data);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching delegation data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOwner) {
      fetchData();
    }
  }, [isOwner]);

  const handleGenerateSuggestions = async () => {
    try {
      setGeneratingSuggestions(true);
      const response = await aiAPI.suggestDelegations();
      setSuggestions(response.data.suggestions);
      toast.success('Sugerencias generadas');
    } catch (error) {
      toast.error('Error al generar sugerencias');
    } finally {
      setGeneratingSuggestions(false);
    }
  };

  const handleDelegate = async (taskId, toUserId, handoffChecklist = []) => {
    try {
      await delegationsAPI.create({
        task_id: taskId,
        to_user_id: toUserId,
        reason: 'Delegación según regla del 80%',
        handoff_checklist: handoffChecklist
      });
      toast.success('Tarea delegada');
      fetchData();
    } catch (error) {
      toast.error('Error al delegar');
    }
  };

  if (!isOwner) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h2 className="text-lg font-medium text-gray-900 mb-2">Acceso Restringido</h2>
        <p className="text-gray-500">Solo el Owner puede acceder a la cola de delegación.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-gray-700" />
            Delegation Queue
          </h1>
          <p className="text-gray-500 mt-1">
            Regla del 80%: Delega toda tarea que otro pueda ejecutar al ≥ 80%
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleGenerateSuggestions}
            disabled={generatingSuggestions}
            className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white"
            data-testid="generate-suggestions-btn"
          >
            {generatingSuggestions ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analizando...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Sugerir Delegaciones (IA)
              </>
            )}
          </Button>
          <Button 
            variant="outline" 
            size="icon"
            className="border-gray-200 hover:bg-gray-100"
            onClick={fetchData}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* AI Suggestions */}
      {suggestions.length > 0 && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-medium text-gray-900">Sugerencias de IA</h2>
          </div>
          <div className="space-y-4">
            {suggestions.map((suggestion, idx) => (
              <div 
                key={idx}
                className="p-4 bg-white rounded-lg border border-purple-100 shadow-sm"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-gray-900">{suggestion.task_title}</h3>
                  {suggestion.confidence_80_percent && (
                    <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">
                      ≥80% viable
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-3">{suggestion.delegate_reason}</p>
                
                <div className="flex items-center gap-2 mb-3">
                  <UserPlus className="w-4 h-4 text-purple-500" />
                  <span className="text-sm text-purple-700 font-medium">{suggestion.suggested_delegate}</span>
                </div>

                {suggestion.handoff_checklist?.length > 0 && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-xs font-medium text-gray-500 mb-2 block">Checklist de Handoff:</span>
                    <ul className="space-y-1">
                      {suggestion.handoff_checklist.map((item, i) => (
                        <li key={i} className="text-xs text-gray-600 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-purple-400 rounded-full" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex gap-2">
                  <Select onValueChange={(userId) => handleDelegate(suggestion.task_id, userId, suggestion.handoff_checklist)}>
                    <SelectTrigger className="flex-1 bg-white border-gray-200">
                      <SelectValue placeholder="Asignar a..." />
                    </SelectTrigger>
                    <SelectContent>
                      {users.filter(u => u.role !== 'owner').map((user) => (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {user.name} ({user.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Delegations */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-amber-500" />
          Delegaciones Pendientes
        </h2>
        
        {delegations.filter(d => d.status === 'pending').length > 0 ? (
          <div className="space-y-3">
            {delegations.filter(d => d.status === 'pending').map((delegation) => (
              <div 
                key={delegation.delegation_id}
                className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-100"
              >
                <div>
                  <p className="text-gray-900 font-medium">{delegation.task_id}</p>
                  <p className="text-sm text-gray-600">{delegation.reason}</p>
                </div>
                <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded">
                  Pendiente
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No hay delegaciones pendientes</p>
        )}
      </div>

      {/* Completed Delegations */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          Delegaciones Completadas
        </h2>
        
        {delegations.filter(d => d.status === 'completed').length > 0 ? (
          <div className="space-y-3">
            {delegations.filter(d => d.status === 'completed').map((delegation) => (
              <div 
                key={delegation.delegation_id}
                className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border border-emerald-100"
              >
                <div>
                  <p className="text-gray-900 font-medium">{delegation.task_id}</p>
                  <p className="text-sm text-gray-600">
                    Delegado a: {delegation.to_user_id}
                  </p>
                </div>
                <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded">
                  Completada
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No hay delegaciones completadas</p>
        )}
      </div>

      {/* My Tasks to Delegate */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Mis Tareas (Candidatas a Delegar)</h2>
        
        {tasks.length > 0 ? (
          <div className="space-y-3">
            {tasks.slice(0, 10).map((task) => (
              <div 
                key={task.task_id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100"
              >
                <div className="flex-1">
                  <p className="text-gray-900">{task.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500 font-mono">{task.effort}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      task.priority === 'high' ? 'bg-red-100 text-red-700' :
                      task.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {task.priority}
                    </span>
                  </div>
                </div>
                <Select onValueChange={(userId) => handleDelegate(task.task_id, userId)}>
                  <SelectTrigger className="w-40 bg-white border-gray-200">
                    <SelectValue placeholder="Delegar a..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.filter(u => u.role !== 'owner').map((user) => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No tienes tareas pendientes</p>
        )}
      </div>
    </div>
  );
};

export default DelegationPage;
